import React, { useRef, useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import { APP_NAME, APP_TAGLINE, DEFAULT_NOTE_COLORS } from '../constants';

const Settings = () => {
  const { 
    showArchived, setShowArchived, showArchivedWorkspaces, setShowArchivedWorkspaces,
    exportAllData, importData, resetPreferences, syncSettings, setSyncSettings,
    syncState, openConfirm, templates, createTemplate, updateTemplate, deleteTemplate,
    createNoteFromTemplate, testScriptConnection
  } = useStore();
  
  const fileInputRef = useRef(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  const Section = ({ title, children, action }) => (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-5 sm:mb-6">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center gap-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500">{title}</h3>
        {action}
      </div>
      <div className="p-4 sm:p-6 space-y-4">{children}</div>
    </div>
  );

  const Toggle = ({ label, value, onChange, description }) => (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="font-medium text-gray-900">{label}</div>
        {description && <div className="text-xs text-gray-500">{description}</div>}
      </div>
      <button 
        onClick={() => onChange(!value)}
        className={`w-14 h-8 min-w-14 rounded-full transition-colors relative ${value ? 'bg-blue-600' : 'bg-gray-200'}`}
      >
        <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-sm transition-all ${value ? 'left-7' : 'left-1'}`} />
      </button>
    </div>
  );

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    openConfirm({
      title: 'Import Data',
      message: 'Choose how you want to import your data. Merge will keep existing notes, Replace will clear everything first.',
      confirmLabel: 'Merge Data',
      onConfirm: () => importData(file, 'merge')
    });
  };

  const handleNewTemplate = () => {
    setEditingTemplate({
      name: 'New Template',
      type: 'text',
      presetTitle: '',
      presetTags: [],
      presetColorKey: 'white',
      content: ''
    });
  };

  const saveTemplate = async () => {
    if (editingTemplate.id) {
      await updateTemplate(editingTemplate);
    } else {
      await createTemplate(editingTemplate);
    }
    setEditingTemplate(null);
  };

  const runTest = async () => {
    setIsTesting(true);
    setTestStatus(null);
    try {
      await testScriptConnection(syncSettings.scriptUrl);
      setTestStatus({ ok: true, message: "Connected successfully!" });
    } catch (err) {
      setTestStatus({ ok: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-2 sm:py-6 min-w-0">
      <div className="mb-5 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your workspace preferences and cloud sync.</p>
      </div>

      <Section 
        title="Templates" 
        action={
          <button onClick={handleNewTemplate} className="min-h-11 px-2 text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1">
            <SafeIcon name="Plus" /> New Template
          </button>
        }
      >
        <div className="space-y-3">
          {templates.length === 0 ? (
            <div className="text-center py-4 text-gray-400 text-sm italic">No templates created yet.</div>
          ) : (
            templates.map(tmpl => (
              <div key={tmpl.id} className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 group">
                <div>
                  <div className="font-bold text-gray-800">{tmpl.name}</div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-widest">{tmpl.type} note</div>
                </div>
                <div className="flex gap-1 opacity-100 min-[1100px]:opacity-0 min-[1100px]:group-hover:opacity-100 transition">
                  <button onClick={() => createNoteFromTemplate(tmpl)} className="min-h-11 px-3 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition">
                    Use
                  </button>
                  <button onClick={() => setEditingTemplate(tmpl)} className="min-w-11 min-h-11 p-2 text-gray-400 hover:text-blue-500 transition rounded-xl">
                    <SafeIcon name="Edit2" className="text-xs" />
                  </button>
                  <button 
                    onClick={() => openConfirm({
                      title: 'Delete Template?',
                      message: `Remove template "${tmpl.name}"?`,
                      danger: true,
                      onConfirm: () => deleteTemplate(tmpl.id)
                    })}
                    className="min-w-11 min-h-11 p-2 text-gray-400 hover:text-red-500 transition rounded-xl"
                  >
                    <SafeIcon name="Trash2" className="text-xs" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </Section>

      {editingTemplate && (
        <div className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 safe-top safe-bottom">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-lg w-full p-5 sm:p-8 space-y-5 sm:space-y-6 max-h-[92dvh] overflow-y-auto">
            <h2 className="text-2xl font-black text-gray-900">Template Editor</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Template Name</label>
                <input value={editingTemplate.name} onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})} className="w-full min-h-11 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Note Type</label>
                  <select value={editingTemplate.type} onChange={e => setEditingTemplate({...editingTemplate, type: e.target.value})} className="w-full min-h-11 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none" >
                    <option value="text">Plain Text</option>
                    <option value="checklist">Checklist</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Color</label>
                  <div className="flex gap-1 pt-1">
                    {DEFAULT_NOTE_COLORS.map(c => (
                      <button key={c.key} onClick={() => setEditingTemplate({...editingTemplate, presetColorKey: c.key})} className={`w-9 h-9 rounded-full border-2 ${editingTemplate.presetColorKey === c.key ? 'border-gray-800' : 'border-transparent'}`} style={{ backgroundColor: c.hex }} />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Default Content</label>
                <textarea value={editingTemplate.content} onChange={e => setEditingTemplate({...editingTemplate, content: e.target.value})} placeholder="Body text for new notes..." className="w-full h-32 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button onClick={() => setEditingTemplate(null)} className="flex-1 py-3 px-4 rounded-2xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={saveTemplate} className="flex-1 py-3 px-4 rounded-2xl bg-blue-600 text-white font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition">Save Template</button>
            </div>
          </div>
        </div>
      )}

      <Section title="Google Sheets Sync">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">Apps Script Web App URL</label>
              <button 
                onClick={runTest}
                disabled={isTesting || !syncSettings.scriptUrl}
                className="min-h-11 px-2 text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-30"
              >
                {isTesting ? "Testing..." : "Test Connection"}
              </button>
            </div>
            <input 
              type="text" 
              value={syncSettings.scriptUrl} 
              onChange={e => setSyncSettings({ scriptUrl: e.target.value })} 
              placeholder="https://script.google.com/macros/s/.../exec" 
              className="w-full min-h-11 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono text-base" 
            />
            {testStatus && (
              <div className={`p-3 rounded-lg text-xs font-bold flex items-start gap-2 ${testStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <SafeIcon name={testStatus.ok ? "CheckCircle" : "AlertCircle"} className="mt-0.5 shrink-0" />
                <span className="leading-normal">{testStatus.message}</span>
              </div>
            )}
          </div>
          <Toggle 
            label="Auto Sync on Activation" 
            description="Sync automatically when you return to the app." 
            value={syncSettings.autoSync} 
            onChange={v => setSyncSettings({ autoSync: v })} 
          />
        </div>
      </Section>

      <Section title="Data Management">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={exportAllData} className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition text-left group">
            <div>
              <div className="font-bold text-blue-700">Export JSON</div>
              <div className="text-xs text-blue-600/70">Download all your local data.</div>
            </div>
            <SafeIcon name="Download" className="text-blue-500 group-hover:scale-110 transition" />
          </button>
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition text-left group">
            <div>
              <div className="font-bold text-gray-700">Import JSON</div>
              <div className="text-xs text-gray-500">Upload data from another device.</div>
            </div>
            <SafeIcon name="Upload" className="text-gray-400 group-hover:scale-110 transition" />
            <input type="file" ref={fileInputRef} onChange={handleImport} className="hidden" accept=".json" />
          </button>
        </div>
      </Section>
    </div>
  );
};

export default Settings;